import { useState } from "react";
import { getAllContactUs } from "../../api/Emails/GetAllContactUs";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getAllNewsLetter } from "../../api/Emails/GetAllNewsLetter";
const Mails = () => {
    const [activeTab, setActiveTab] = useState<'contacts' | 'newsletter'>('contacts');
    const [page, setPage] = useState(1);
    const [limit] = useState(10);

    const { data, isLoading } = useQuery({
        queryKey: ["contact-us", page],
        queryFn: () => getAllContactUs({ page, limit }),
    });

    const { data: newsLetterData, isLoading: newsLetterLoading } = useQuery({
        queryKey: ["news-letter", page],
        queryFn: () => getAllNewsLetter({ page, limit }),
    });
    const newsLetter = newsLetterData?.data?.newsLetters;
    const newsLetterMetaData = newsLetterData?.data?.metaData;
    const contactUs = data?.data?.contactUs;
    const metaData = data?.data?.metaData;
    const handleTabChange = (tab: 'contacts' | 'newsletter') => {
        setActiveTab(tab);
        setPage(1);
    };

    const renderPagination = (totalPages: number | undefined) => (
        <div className="flex justify-center mt-8">
            <div className="flex items-center space-x-2">
                <button
                    onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                    disabled={page === 1}
                    className={`px-3 py-2 rounded-md transition-colors duration-200 flex items-center ${
                        page === 1
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex space-x-2">
                    {[...Array(totalPages || 1)].map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setPage(index + 1)}
                            className={`px-4 py-2 rounded-md transition-colors duration-200 ${
                                page === index + 1
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {index + 1}
                        </button>
                    ))}
                </div>

                <button
                    onClick={() => setPage(prev => Math.min(prev + 1, totalPages || 1))}
                    disabled={page === (totalPages || 1)}
                    className={`px-3 py-2 rounded-md transition-colors duration-200 flex items-center ${
                        page === (totalPages || 1)
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    <ChevronRight className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
    // Loading skeleton component
    const SkeletonCard = () => (
        <div className="bg-gray-100 rounded-lg p-6 h-[250px] animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-20 bg-gray-200 rounded w-full"></div>
        </div>
    );

    return (
        <div className="p-6">
            <div className="flex justify-between items-center py-7">
                <h1 className="text-burntBlack font-bold text-[32px]">Mails</h1>
            </div>

            <div className="flex space-x-4 mb-6">
                <button
                    onClick={() => handleTabChange('contacts')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        activeTab === 'contacts'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    Contact Messages
                </button>
                <button
                    onClick={() => handleTabChange('newsletter')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        activeTab === 'newsletter'
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                >
                    Newsletter Subscribers
                </button>
            </div>

            {activeTab === 'contacts' ? (
                // Contact Messages Content
                isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[...Array(4)].map((_, index) => (
                            <SkeletonCard key={index} />
                        ))}
                    </div>
                ) : (
                    <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {contactUs?.map((contact) => (
                            <div
                                key={contact.id}
                                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                            >
                                <div className="flex justify-between items-start mb-4">
                                    <h2 className="text-xl font-semibold text-gray-800">
                                        {contact.name}
                                    </h2>
                                    <span className="text-xs text-gray-500">
                                        {format(new Date(contact.createdAt), 'PPpp')}
                                    </span>
                                </div>

                                <div className="space-y-2 mb-4">
                                    <p className="text-sm text-gray-600 flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        {contact.email}
                                    </p>
                                    <p className="text-sm text-gray-600 flex items-center">
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        {contact.phone}
                                    </p>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-gray-700">{contact.message}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    {renderPagination(metaData?.totalPages)}
                                        </>
                )
            ) : (
                // Newsletter Content
                newsLetterLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[...Array(4)].map((_, index) => (
                            <SkeletonCard key={index} />
                        ))}
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {newsLetter?.map((subscriber) => (
                                <div 
                                    key={subscriber.id}
                                    className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow duration-300"
                                >
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center space-x-3">
                                            <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                            </svg>
                                            <span className="text-gray-800">{subscriber.email}</span>
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            {format(new Date(subscriber.createdAt), 'PPpp')}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {renderPagination(newsLetterMetaData?.totalPages)}
                    </>
                )
            )}
        </div>
    );
};

export default Mails;